import { useRef, useState, type ReactNode } from "react";
import { Maximize2 } from "lucide-react";
import { BUBBLE_SIZE_CLASS } from "./bubble";

export type BubbleCorner = "tl" | "tr" | "bl" | "br";

function cornerClass(corner: BubbleCorner): string {
  switch (corner) {
    case "tl": return "top-4 left-4 ";
    case "tr": return "top-4 right-4 ";
    case "bl": return "bottom-4 left-4 ";
    case "br": return "bottom-4 right-4 ";
  }
}

export function oppositeCorner(corner: BubbleCorner): BubbleCorner {
  switch (corner) {
    case "tl": return "br";
    case "tr": return "bl";
    case "bl": return "tr";
    case "br": return "tl";
  }
}

interface Props {
  /** Avatar content (usually an `<AvatarVideoArea hideControls />`). */
  children: ReactNode;
  /** Initial anchor corner. Defaults to "tr". */
  initialCorner?: BubbleCorner;
  /** Current bubble size in px from `useBubbleSize`. */
  size: { width: number; height: number };
  /** Resize handle callbacks from `useBubbleSize`. */
  onResizeStart: (e: React.PointerEvent<HTMLElement>) => void;
  onResizeMove: (e: React.PointerEvent<HTMLElement>) => void;
  onResizeEnd: (e: React.PointerEvent<HTMLElement>) => void;
  /** Notified when the user drops the bubble into a new corner. */
  onCornerChange?: (corner: BubbleCorner) => void;
}

/**
 * Draggable + resizable corner bubble used by every provider frame to
 * host the avatar when a document (or shared screen) occupies the primary
 * pane. Owns pointer-drag → snap-to-corner and translate offset while
 * dragging; sizing is delegated to `useBubbleSize` so both frames share
 * the same clamped dimensions.
 */
export function DraggableAvatarBubble({
  children,
  initialCorner = "tr",
  size,
  onResizeStart,
  onResizeMove,
  onResizeEnd,
  onCornerChange,
}: Props) {
  const [corner, setCorner] = useState<BubbleCorner>(initialCorner);
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  return (
    <div
      ref={wrapRef}
      className={
        "absolute " +
        BUBBLE_SIZE_CLASS +
        " rounded-2xl overflow-hidden border-2 border-background shadow-2xl bg-background touch-none select-none cursor-grab active:cursor-grabbing " +
        cornerClass(corner)
      }
      style={{
        ...(size.width > 0 ? { width: size.width, height: size.height } : {}),
        ...(drag
          ? { transform: `translate(${drag.dx}px, ${drag.dy}px)`, transition: "none" }
          : {}),
      }}
      onPointerDown={(e) => {
        dragStateRef.current = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          moved: false,
        };
      }}
      onPointerMove={(e) => {
        const s = dragStateRef.current;
        if (!s || s.pointerId !== e.pointerId) return;
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;
        if (!s.moved && Math.hypot(dx, dy) < 6) return;
        if (!s.moved) {
          s.moved = true;
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        }
        setDrag({ dx, dy });
      }}
      onPointerUp={(e) => {
        const s = dragStateRef.current;
        dragStateRef.current = null;
        if (!s || !s.moved) {
          setDrag(null);
          return;
        }
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        const wrap = wrapRef.current;
        const parent = wrap?.parentElement;
        if (wrap && parent) {
          const wr = wrap.getBoundingClientRect();
          const pr = parent.getBoundingClientRect();
          const cx = wr.left + wr.width / 2 - pr.left;
          const cy = wr.top + wr.height / 2 - pr.top;
          const left = cx < pr.width / 2;
          const top = cy < pr.height / 2;
          const next: BubbleCorner = top ? (left ? "tl" : "tr") : left ? "bl" : "br";
          setCorner(next);
          onCornerChange?.(next);
        }
        setDrag(null);
      }}
      onPointerCancel={() => {
        dragStateRef.current = null;
        setDrag(null);
      }}
    >
      <div className={drag ? "pointer-events-none h-full w-full" : "h-full w-full"}>
        {children}
      </div>
      <button
        type="button"
        aria-label="Resize bubble"
        className="absolute bottom-1 right-1 z-30 p-1 rounded-full bg-black/40 text-white hover:bg-black/60 cursor-nwse-resize touch-none"
        onPointerDown={(e) => {
          e.stopPropagation();
          onResizeStart(e);
        }}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
      >
        <Maximize2 className="h-3 w-3" />
      </button>
    </div>
  );
}